import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { fmtCentavos, fmtMesAno, fmtMesCurto } from '@/pages/admin/shared';
import { useFaturamentoEstimado, fmtTaxa } from '@/pages/admin/faturamento-data';

const MESES_PROJETADOS = 5;

export default function AdminFaturamentoEstimadoPage() {
  const { isSuperuser } = useAuth();
  const { estimativa, carregando, erro, recarregar } = useFaturamentoEstimado(MESES_PROJETADOS);

  if (!isSuperuser) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Esta área é reservada para o superusuário administrador da plataforma. Faça login com as
            credenciais de administrador para acessar.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const projecao = estimativa.meses;
  const maxEstimado = Math.max(1, ...projecao.map((f) => f.otimista));
  const totalProjetado = projecao.reduce((s, f) => s + f.base, 0);
  const totalOtimista = projecao.reduce((s, f) => s + f.otimista, 0);
  const totalPessimista = projecao.reduce((s, f) => s + f.pessimista, 0);

  const periodo =
    projecao.length > 0
      ? `${fmtMesAno(projecao[0].mes)} a ${fmtMesAno(projecao[projecao.length - 1].mes)}`
      : 'sem período projetado';

  const semMovimento = estimativa.crescimento === 0 && estimativa.churn === 0;

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl md:text-2xl font-bold text-foreground-900">
              Faturamento Estimado
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-accent-500 text-background-50">
              Super Admin
            </span>
          </div>
          <p className="text-sm text-foreground-500">
            Projeção a partir do MRR atual e das taxas observadas — {periodo}.
          </p>
        </div>

        {carregando ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center gap-3 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-xl"></i>
              <span className="text-sm">Projetando a receita...</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {erro && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-600"></i>
                  <span className="text-sm text-red-700">Erro ao projetar a receita: {erro}</span>
                </div>
                <button
                  onClick={recarregar}
                  className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Tentar novamente
                </button>
              </div>
            )}

            {/* Ponto de partida da projeção — o estado de hoje, não a estimativa */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                {
                  label: 'MRR atual',
                  valor: fmtCentavos(estimativa.mrr),
                  desc: `${estimativa.clientes} assinatura(s) em vigor`,
                  corBg: 'bg-primary-50',
                  corIcon: 'text-primary-600',
                  icone: 'ri-repeat-line',
                },
                {
                  label: 'Ticket médio',
                  valor: fmtCentavos(estimativa.ticket_medio),
                  desc: 'Por assinatura em vigor',
                  corBg: 'bg-secondary-50',
                  corIcon: 'text-secondary-600',
                  icone: 'ri-price-tag-3-line',
                },
                {
                  label: 'Crescimento observado',
                  valor: fmtTaxa(estimativa.crescimento),
                  desc: `Média de ${estimativa.meses_de_historico} mês(es) fechado(s)`,
                  corBg: 'bg-green-50',
                  corIcon: 'text-green-600',
                  icone: 'ri-arrow-up-circle-line',
                },
                {
                  label: 'Churn observado',
                  valor: fmtTaxa(estimativa.churn),
                  desc: `${estimativa.em_teste} conta(s) em teste`,
                  corBg: 'bg-amber-50',
                  corIcon: 'text-amber-600',
                  icone: 'ri-arrow-down-circle-line',
                },
              ].map((card) => (
                <div
                  key={card.label}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-4"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className={`w-8 h-8 rounded-lg ${card.corBg} flex items-center justify-center`}
                    >
                      <i className={`${card.icone} ${card.corIcon} text-sm`}></i>
                    </div>
                    <p className="text-xs font-medium text-foreground-500">{card.label}</p>
                  </div>
                  <p className="text-xl font-bold text-foreground-900 mt-1">{card.valor}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">{card.desc}</p>
                </div>
              ))}
            </div>

            {/*
              As taxas saem do próprio histórico, e não de um "+15%" no código.
              Sem meses fechados com movimento, a projeção é o MRR repetido — e
              dizer isso é mais útil que inventar uma curva.
            */}
            <div className="flex items-start gap-3 p-4 rounded-lg bg-primary-50/50 border border-primary-200/50">
              <i className="ri-information-line text-primary-600 text-sm mt-0.5"></i>
              <div>
                <p className="text-sm font-medium text-primary-800">Como a projeção é calculada</p>
                <p className="text-xs text-primary-600 mt-0.5 leading-relaxed">
                  O ponto de partida é o MRR das assinaturas em vigor (ativas e inadimplentes;
                  contas em teste ficam de fora). As taxas de crescimento e churn são as observadas
                  nos meses fechados, não constantes fixas. O cenário base aplica as duas, o
                  otimista ignora o churn e o pessimista ignora as novas assinaturas.
                  {semMovimento &&
                    ' Como ainda não houve movimento de assinaturas no histórico, os três cenários repetem o MRR atual.'}
                </p>
              </div>
            </div>

            {/* Totais projetados */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  label: 'Projeção do período',
                  valor: fmtCentavos(totalProjetado),
                  desc: 'Cenário base',
                  corBg: 'bg-primary-50',
                  corIcon: 'text-primary-600',
                  icone: 'ri-funds-line',
                },
                {
                  label: 'Cenário otimista',
                  valor: fmtCentavos(totalOtimista),
                  desc: `Crescimento de ${fmtTaxa(estimativa.crescimento)} ao mês, sem churn`,
                  corBg: 'bg-green-50',
                  corIcon: 'text-green-600',
                  icone: 'ri-arrow-up-circle-line',
                },
                {
                  label: 'Cenário pessimista',
                  valor: fmtCentavos(totalPessimista),
                  desc: `Churn de ${fmtTaxa(estimativa.churn)} ao mês, sem novas`,
                  corBg: 'bg-amber-50',
                  corIcon: 'text-amber-600',
                  icone: 'ri-arrow-down-circle-line',
                },
              ].map((card) => (
                <div
                  key={card.label}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-5"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div
                      className={`w-8 h-8 rounded-lg ${card.corBg} flex items-center justify-center`}
                    >
                      <i className={`${card.icone} ${card.corIcon} text-sm`}></i>
                    </div>
                    <p className="text-xs font-medium text-foreground-500">{card.label}</p>
                  </div>
                  <p className="text-2xl font-bold text-foreground-900 mt-2">{card.valor}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">{card.desc}</p>
                </div>
              ))}
            </div>

            {/* Gráfico comparativo */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg">
              <div className="px-5 py-4 border-b border-background-200/70">
                <h3 className="text-base font-semibold text-foreground-900">
                  Projeção de receita — {periodo}
                </h3>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-primary-500"></div>
                    <span className="text-xs text-foreground-600">Base</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-green-500"></div>
                    <span className="text-xs text-foreground-600">Otimista</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-amber-500"></div>
                    <span className="text-xs text-foreground-600">Pessimista</span>
                  </div>
                </div>
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-end gap-3 h-48">
                  {projecao.map((f) => {
                    const altBase = Math.max((f.base / maxEstimado) * 100, 6);
                    const altOtimista = Math.max((f.otimista / maxEstimado) * 100, 6);
                    const altPessimista = Math.max((f.pessimista / maxEstimado) * 100, 6);
                    return (
                      <div
                        key={f.mes}
                        className="flex-1 flex flex-col items-center gap-2 min-w-0 h-full justify-end"
                      >
                        <span className="text-[10px] font-semibold text-foreground-700 whitespace-nowrap">
                          {fmtCentavos(f.base)}
                        </span>
                        <div className="flex items-end gap-1 w-full max-w-[80px] justify-center">
                          <div
                            className="flex-1 max-w-[18px] rounded-t-sm bg-primary-500 transition-all hover:bg-primary-600 cursor-pointer relative group"
                            style={{ height: `${altBase}%` }}
                          >
                            <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-foreground-900 text-background-50 text-[10px] rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              Base: {fmtCentavos(f.base)}
                            </div>
                          </div>
                          <div
                            className="flex-1 max-w-[18px] rounded-t-sm bg-green-500 transition-all hover:bg-green-600 cursor-pointer relative group"
                            style={{ height: `${altOtimista}%` }}
                          >
                            <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-foreground-900 text-background-50 text-[10px] rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              Otimista: {fmtCentavos(f.otimista)}
                            </div>
                          </div>
                          <div
                            className="flex-1 max-w-[18px] rounded-t-sm bg-amber-500 transition-all hover:bg-amber-600 cursor-pointer relative group"
                            style={{ height: `${altPessimista}%` }}
                          >
                            <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-foreground-900 text-background-50 text-[10px] rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              Pessimista: {fmtCentavos(f.pessimista)}
                            </div>
                          </div>
                        </div>
                        <span className="text-xs font-medium text-foreground-500">
                          {fmtMesCurto(f.mes)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Tabela de projeção */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Mês
                      </th>
                      <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Cenário base
                      </th>
                      <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Cenário otimista
                      </th>
                      <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Cenário pessimista
                      </th>
                      <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Clientes prev.
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/50">
                    {projecao.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-12 text-center text-sm text-foreground-400"
                        >
                          Nenhum mês projetado.
                        </td>
                      </tr>
                    ) : (
                      projecao.map((f) => (
                        <tr key={f.mes} className="hover:bg-background-100/30 transition-colors">
                          <td className="px-4 py-3">
                            <span className="text-sm font-medium text-foreground-800">
                              {fmtMesAno(f.mes)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-semibold text-foreground-800">
                              {fmtCentavos(f.base)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-semibold text-green-700">
                              {fmtCentavos(f.otimista)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-sm font-semibold text-amber-700">
                              {fmtCentavos(f.pessimista)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-sm text-foreground-600">{f.clientes}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-background-200/70 bg-background-100/30">
                      <td className="px-4 py-3 text-sm font-semibold text-foreground-700">Total</td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-foreground-900">
                        {fmtCentavos(totalProjetado)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-green-700">
                        {fmtCentavos(totalOtimista)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-amber-700">
                        {fmtCentavos(totalPessimista)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
